/**
 * BOP Map Event API
 *
 * Allows Webflow (or any external code) to control the map widget.
 *
 * --- Usage from Webflow custom code ---
 *
 * BOPMap.flyTo("jamaica-bay");
 * BOPMap.flyTo({ center: [-74.02, 40.62], zoom: 12, pitch: 60 });
 * BOPMap.highlight("upper-bay");
 * BOPMap.hover("upper-bay");    // glow as if hovered; BOPMap.hover(null) clears
 * BOPMap.showLabels(true);
 * BOPMap.showLabels(false);
 * BOPMap.reset();
 * BOPMap.setOysterCount("jamaica-bay", 50000000);
 * BOPMap.reveal("water-bodies");
 * BOPMap.reveal("labels");
 * BOPMap.reveal("upper-bay");
 * BOPMap.setMode("explore");   // enable zoom/pan/rotate
 * BOPMap.setMode("static");    // lock map (default)
 * BOPMap.on("waterBodyClick", (id) => { ... });
 * BOPMap.on("ready", () => { ... });
 *
 * --- DOM events (for Webflow Interactions) ---
 *
 * document.dispatchEvent(new CustomEvent("bop-map:flyTo", {
 *   detail: { target: "jamaica-bay" }
 * }));
 */

export type FlyToTarget =
  | string
  | {
      center: [number, number];
      zoom?: number;
      pitch?: number;
      bearing?: number;
      duration?: number;
    };

export type MapEventType =
  | "waterBodyClick"
  | "waterBodyHover"
  | "ready"
  | "animationStart"
  | "animationEnd";

export interface BOPMapAPI {
  flyTo: (target: FlyToTarget, options?: { duration?: number }) => void;
  highlight: (waterBodyId: string) => void;
  /** Glow a water body as if hovered (null clears). Also fired by [data-bop-wb] cards. */
  hover: (waterBodyId: string | null) => void;
  showLabels: (visible: boolean) => void;
  reset: () => void;
  setOysterCount: (waterBodyId: string, count: number) => void;
  reveal: (target: string) => void;
  setMode: (mode: "static" | "explore") => void;
  on: (event: MapEventType, callback: (data?: unknown) => void) => void;
  off: (event: MapEventType, callback: (data?: unknown) => void) => void;
}

type Listener = (data?: unknown) => void;

export function createMapEventBus() {
  const listeners = new Map<MapEventType, Set<Listener>>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api: BOPMapAPI = {
    flyTo: () => {},
    highlight: () => {},
    hover: () => {},
    showLabels: () => {},
    reset: () => {},
    setOysterCount: () => {},
    reveal: () => {},
    setMode: () => {},
    on: (event, callback) => {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(callback);
    },
    off: (event, callback) => {
      listeners.get(event)?.delete(callback);
    },
  };

  (window as Record<string, unknown>).BOPMap = api;

  // DOM custom event listeners for Webflow Interactions
  const domEvents = [
    "flyTo", "highlight", "hover", "showLabels", "reset",
    "setOysterCount", "reveal", "setMode",
  ] as const;

  for (const eventName of domEvents) {
    document.addEventListener(`bop-map:${eventName}`, ((e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (eventName === "flyTo") api.flyTo(detail.target, detail);
      else if (eventName === "setOysterCount") api.setOysterCount(detail.waterBodyId, detail.count);
      else if (eventName === "showLabels") api.showLabels(detail.visible ?? true);
      else if (eventName === "highlight") api.highlight(detail.waterBodyId ?? detail.target);
      else if (eventName === "hover") api.hover(detail.waterBodyId ?? null);
      else if (eventName === "reveal") api.reveal(detail.target);
      else if (eventName === "setMode") api.setMode(detail.mode ?? "static");
      else if (eventName === "reset") api.reset();
    }) as EventListener);
  }

  return {
    emit: (event: MapEventType, data?: unknown) => {
      listeners.get(event)?.forEach((cb) => cb(data));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerHandler: (handlers: Partial<Record<string, any>>) => {
      for (const [key, handler] of Object.entries(handlers)) {
        if (handler && key in api) {
          (api as Record<string, unknown>)[key] = handler;
        }
      }
    },
  };
}
